import {
    createContext,
    type FC,
    Suspense,
    useEffect,
} from "react";
import {
    Outlet,
    useOutletContext,
    useParams,
} from "react-router";
import {
    DialogContent,
    type DialogProps,
    Icon,
    useDialogCreate,
    useLayerRemove,
} from "@onshopfront/components";

type DialogOutputContext = () => void;

interface DialogOutputProps {
    onClose: () => void;
}

export const useDialogOutputContext = (): DialogOutputContext => {
    return useOutletContext<DialogOutputContext>();
};

export const DialogOutput: FC<DialogOutputProps> = ({ onClose }) => (
    <Outlet context={onClose as DialogOutputContext} />
);

export const DialogRouteContext = createContext<Record<string, string | undefined>>({});

const DialogRoute: FC<DialogProps> = ({ content, ...props }) => {
    const params = useParams();
    const context = useDialogOutputContext();

    const createDialog = useDialogCreate();
    const removeLayer = useLayerRemove();

    useEffect(() => {
        const id = createDialog({
            blockClosing: true,
            ...props,
            content     : (
                <DialogRouteContext.Provider value={params}>
                    <Suspense
                        fallback={(
                            <DialogContent className="p-12 text-2xl">
                                <Icon
                                    icon={[ "fal", "spinner-third" ]}
                                    spin
                                />
                            </DialogContent>
                        )}
                    >
                        {content}
                    </Suspense>
                </DialogRouteContext.Provider>
            ),
        }, () => {
            context();
        });

        return () => {
            removeLayer(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ createDialog, removeLayer ]); // Purposefully empty (this component should have hard coded props)

    return null;
};

export default DialogRoute;
